<template>
    <div class="mb-3">
        <label class="form-label">名前</label>
        <input type="text" class="form-control" v-model="user.name" :disabled="!hasManagePermissionForEdit"/>
    </div>
    <div class="mb-3">
        <label class="form-label">メールアドレス</label>
        <input type="text" class="form-control" :disabled="isEdit || !hasManagePermissionForEdit" v-model="user.email"/>
    </div>
    <div class="mb-3">
        <label class="form-label">操作権限</label>
        <div class="d-flex flex-column gap-3 mt-2">
            <!-- Manage Permission Toggle -->
            <div class="form-check form-switch">
                <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="userManagePermission"
                    v-model="hasManagePermission"
                    @change="updatePermissions"
                    :disabled="!hasManagePermissionForEdit"
                >
                <label class="form-check-label" for="userManagePermission">
                    管理権限
                    <small class="text-muted d-block">所属組織とメンバーデータのCRUD操作が可能</small>
                </label>
            </div>

            <!-- Export Permission Toggle -->
            <div class="form-check form-switch">
                <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="userExportPermission"
                    v-model="hasExportPermission"
                    @change="updatePermissions"
                    :disabled="!hasManagePermissionForEdit"
                >
                <label class="form-check-label" for="userExportPermission">
                    ファイル出力権限
                    <small class="text-muted d-block">CSVファイル等のエクスポート操作が可能</small>
                </label>
            </div>

            <!-- Import Permission Toggle -->
            <div class="form-check form-switch">
                <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="userImportPermission"
                    v-model="hasImportPermission"
                    @change="updatePermissions"
                    :disabled="!hasManagePermissionForEdit"
                >
                <label class="form-check-label" for="userImportPermission">
                    ファイル編集権限
                    <small class="text-muted d-block">CSVファイル等のインポート操作が可能</small>
                </label>
            </div>

            <!-- Detail View Permission Toggle -->
            <div class="form-check form-switch">
                <input
                    class="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="userDetailPermission"
                    v-model="hasDetailPermission"
                    @change="updatePermissions"
                    :disabled="!hasManagePermissionForEdit"
                >
                <label class="form-check-label" for="userDetailPermission">
                    詳細閲覧権限
                    <small class="text-muted d-block">宿泊ページとイベント詳細ページの閲覧が可能（rampsドメインのユーザーには作成時に自動付与）</small>
                </label>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import User from '../../../models/User';

const props = defineProps({
    modelValue: {
        type: User
    },
    isEdit: {
        type: Boolean,
        default: false
    },
    currentAdminPermissions: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['update:modelValue']);

const user = computed({
    get: () => props.modelValue,
    set: value => emit('update:modelValue', value)
});

// Permission states
const hasManagePermission = ref(false);
const hasExportPermission = ref(false);
const hasImportPermission = ref(false);
const hasDetailPermission = ref(false);

// Check if current admin has MANAGE permission
const hasManagePermissionForEdit = computed(() => {
    return props.currentAdminPermissions.some(p => p.permissionType === 'MANAGE');
});

// Initialize permissions from user data
watch(() => props.modelValue, (newUser) => {
    if (newUser && newUser.permissions) {
        hasManagePermission.value = newUser.permissions.some(p => p.permissionType === 'MANAGE');
        hasExportPermission.value = newUser.permissions.some(p => p.permissionType === 'VIEW');
        hasImportPermission.value = newUser.permissions.some(p => p.permissionType === 'EDIT');
        hasDetailPermission.value = newUser.permissions.some(p => p.permissionType === 'DETAIL');
    } else {
        hasManagePermission.value = false;
        hasExportPermission.value = false;
        hasImportPermission.value = false;
        hasDetailPermission.value = false;
    }
}, { immediate: true, deep: true });

// Update permissions in user model
const updatePermissions = () => {
    const permissions = [];
    if (hasManagePermission.value) {
        permissions.push({ permissionType: 'MANAGE' });
    }
    if (hasExportPermission.value) {
        permissions.push({ permissionType: 'VIEW' });
    }
    if (hasImportPermission.value) {
        permissions.push({ permissionType: 'EDIT' });
    }
    if (hasDetailPermission.value) {
        permissions.push({ permissionType: 'DETAIL' });
    }

    user.value = {
        ...user.value,
        permissions: permissions
    };
};
</script>
